<template>
  <div :class="{ 'about-story': story }">
    <h1 v-if="!story" data-scroll-reveal>
      About Me
    </h1>
    <article data-scroll-group data-scroll-step="110">
      <section ref="intro" :id="story ? 'story-about' : undefined" :data-story-chapter="story ? 'About' : undefined" class="about-section about-intro" data-scroll-group data-scroll-step="90">
        <div v-if="story" role="heading" aria-level="2" class="story-about-heading" data-story-eyebrow="03 / About">A little about me</div>
        <div ref="introCopy" class="about-intro-copy" data-scroll-ignore data-scroll-group data-scroll-step="90">
          <p>I'm Han-yu Wang, and you can call me Henry. I was born in <router-link class="place-link" :to="{ path: '/travels', hash: '#tainan' }">Taiwan</router-link>, grew up in <router-link class="place-link" :to="{ path: '/travels', hash: '#shanghai' }">Shanghai</router-link>, and now study in <router-link class="place-link" :to="{ path: '/travels', hash: '#hong-kong' }">Hong Kong</router-link>.</p>

          <p>I'm a fourth-year student majoring in Applied Artificial Intelligence at the University of Hong Kong. Alongside my studies, I'm currently working in AI consulting at PwC.</p>

          <p>Across my studies and work, I'm drawn to things that are still taking shape. I like gradually finding and building structure as I understand them better.</p>
        </div>

        <HomeRoutePortal v-if="story" to="/cv" kind="cv" title="CV" description="Academic and professional experience in one place" label="Open Henry's CV" class="cv-callout" />
        <router-link v-else
          to="/cv"
          class="about-route-card cv-callout"
          aria-label="Open Henry's CV"
        >
          <span class="about-route-icon" aria-hidden="true"><i class="far fa-file-alt"></i></span>
          <span class="about-route-copy">
            <strong>CV</strong>
            <span>Academic and professional experience in one place</span>
          </span>
          <span class="about-route-arrow" aria-hidden="true"><ArrowUpRightIcon /></span>
        </router-link>

        <figure class="photo-frame">
          <img data-enlarge ref="photo" src="/assets/img/henry.webp" alt="Henry at the seaside bus stop by Duoliang, Taimali, Taitung" @load="photoLoaded = true">
          <figcaption class="photo-watermark" :class="{ 'wm-visible': photoLoaded }">
            <span class="wm-zh">多良 · 太麻里 · 台東</span>
            <span class="wm-en">Duoliang · Taimali · Taitung</span>
          </figcaption>
        </figure>
      </section>

      <section class="about-section about-methods" data-scroll-group data-scroll-step="90">
        <section class="about-research-chapter" data-scroll-group :id="story ? 'story-research' : undefined" :data-story-chapter="story ? 'Research' : undefined">
        <h2 v-if="!story">How I Research and Build</h2>
        <div v-else role="heading" aria-level="2" class="story-research-heading" data-story-eyebrow="04 / Research">How we learn and think</div>
        <div ref="researchIntro" class="research-with-image">
          <img
            ref="researchIllustration"
            src="/assets/img/humanAI.webp"
            alt=""
            class="research-with-image-img"
            loading="lazy"
            decoding="async"
            aria-hidden="true"
          >
          <p ref="researchCopy">In my <span class="preview-host" @mouseenter="showPreview('research', $event)" @mouseleave="hidePreview"><router-link to="/research" class="preview-link site-text-link" :class="{ 'is-preview-active': preview === 'research' }"><span class="preview-link-word">Research</span><ArrowUpRightIcon v-if="story" class="preview-link-mark" /></router-link></span>, I work across human learning, AI models, and human-AI interaction. I study how people and machines learn from experience, how what they learn is represented and used, and how it shapes what they can do later. I also study what changes when AI becomes part of learning and thinking, including what a person notices, practises, understands, remembers, and decides, and how systems can be designed to support those processes.</p>
        </div>

        </section>
        <section class="about-build-chapter" data-scroll-group :id="story ? 'story-builds' : undefined" :data-story-chapter="story ? 'Builds' : undefined">
        <div v-if="story" role="heading" aria-level="2" class="story-build-heading" data-story-eyebrow="05 / Builds">Things I build</div>
        <p>I also spend a lot of time building things. I enjoy the open-endedness of it, especially when I can combine ideas, tools, and different parts of a system and see how the structure changes as I work. Some are small tools that automate everyday tasks. Others are larger systems that use AI to help people work with complex information and make better decisions.</p>

        <p>The <span class="preview-host" @mouseenter="showPreview('projects', $event)" @mouseleave="hidePreview"><router-link to="/projects" class="preview-link site-text-link" :class="{ 'is-preview-active': preview === 'projects' }"><span class="preview-link-word">Builds</span><ArrowUpRightIcon v-if="story" class="preview-link-mark" /></router-link></span> page brings together projects from my work and coursework, along with personal projects dating back to secondary school. This website is one of them. I use it to explore how AI can reorganise the same material around what a visitor wants to understand. That process also helps me understand and record myself from different angles.</p>

        <AboutAiEntry :interactive="story" />
        </section>
      </section>

      <section class="about-section about-outside" :id="story ? 'story-life' : undefined" :data-story-chapter="story ? 'Life' : undefined" data-scroll-group data-scroll-step="90">
        <h2 :data-story-eyebrow="story ? '06 / Life' : undefined">Outside work</h2>

        <p>I like the open space by the sea. In <router-link class="place-link" :to="{ path: '/travels', hash: '#napier' }">Hawke's Bay</router-link>, my mother would drive me to the coast near <router-link class="place-link" :to="{ path: '/travels', hash: '#napier' }">Napier</router-link>. In <router-link class="place-link" :to="{ path: '/travels', hash: '#hong-kong' }">Hong Kong</router-link>, I spent time at Kennedy Town Praya during my early university years. When I travel, I also like taking ferries and walking along the waterfront.</p>

        <div v-if="story" class="home-photo-gallery-shell" @mouseleave="leavePhotoGallery">
          <div id="showcaseimages" ref="photoGallery" class="scroll-container home-photo-gallery" role="region" aria-label="Travel photos" tabindex="0"></div>
          <div class="home-photo-gallery-controls" role="group" aria-label="Browse travel photos">
            <button type="button" class="home-photo-gallery-control is-previous" aria-label="Move travel photos left" @pointerenter="hoverPhotoGallery(-1, $event)" @pointerleave="stopPhotoGalleryHover" @click="movePhotoGallery(-1)">
              <svg viewBox="0 0 64 64" fill="currentColor" aria-hidden="true"><path d="M24 3 3 32l21 29 15-12-12-17 12-17z" /></svg>
            </button>
            <button type="button" class="home-photo-gallery-control is-next" aria-label="Move travel photos right" @pointerenter="hoverPhotoGallery(1, $event)" @pointerleave="stopPhotoGalleryHover" @click="movePhotoGallery(1)">
              <svg viewBox="0 0 64 64" fill="currentColor" aria-hidden="true"><path d="m40 3 21 29-21 29-15-12 12-17-12-17z" /></svg>
            </button>
          </div>
        </div>
        <div v-else id="showcaseimages" ref="photoGallery" class="scroll-container home-photo-gallery"></div>

        <div class="about-life-notes" data-scroll-group data-scroll-step="90">
        <p>I like travelling, and I often keep a photograph or a few notes from a trip. Looking back at them helps me remember the people I was with, how the place felt and what my life was like then.</p>

        <HomeRoutePortal v-if="story" to="/travels" kind="travels" open-up title="Travels" description="Places I've lived in, travelled through, and remembered" label="Open Henry's travel map" class="travel-callout" />
        <router-link v-else
          to="/travels"
          class="about-route-card travel-callout"
          aria-label="Open Henry's travel map"
        >
          <span class="about-route-icon" aria-hidden="true"><i class="far fa-compass"></i></span>
          <span class="about-route-copy">
            <strong>Travels</strong>
            <span>Follow the places I've lived in, travelled through, and remembered</span>
          </span>
          <span class="about-route-arrow" aria-hidden="true"><ArrowUpRightIcon /></span>
        </router-link>

        <p>As AI becomes part of how we write and remember, our own thoughts and the memories tied to people, places, and feelings seem especially worth keeping. I keep <span class="preview-host" @mouseenter="showPreview('fragments', $event)" @mouseleave="hidePreview"><router-link to="/fragments" class="preview-link site-text-link" :class="{ 'is-preview-active': preview === 'fragments' }"><span class="preview-link-word">Fragments</span><ArrowUpRightIcon v-if="story" class="preview-link-mark" /></router-link></span> so that I can return to thoughts and memories that would otherwise fade.</p>
        </div>
      </section>

      <section class="about-section about-contact" :id="story ? 'story-contact' : undefined" :data-story-chapter="story ? 'Contact' : undefined" data-scroll-group data-scroll-step="90">
        <h2 :data-story-eyebrow="story ? '07 / Contact' : undefined">Say hello</h2>

        <p>If you want to get in touch, I'd be glad to hear from you.</p>
        <p>For personal messages, write to me at <a href="mailto:whenry6688@gmail.com" class="contact-email site-text-link"><span class="contact-email-text">whenry6688@gmail.com</span><svg v-if="story" class="contact-email-mark" viewBox="0 0 20 16" fill="none" stroke="currentColor" stroke-width="1.15" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="16" height="11" rx="1.5" /><path d="m2.5 4 7.5 5.5L17.5 4" /></svg></a>.</p>
        <p>For academic matters, reach me at <a href="mailto:henry.why@connect.hku.hk" class="contact-email site-text-link"><span class="contact-email-text">henry.why@connect.hku.hk</span><svg v-if="story" class="contact-email-mark" viewBox="0 0 20 16" fill="none" stroke="currentColor" stroke-width="1.15" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="16" height="11" rx="1.5" /><path d="m2.5 4 7.5 5.5L17.5 4" /></svg></a>.</p>

        <HomeRoutePortal v-if="story" to="/traffic" kind="traffic" title="Site traffic" description="A small record of visits and page activity" label="Open site traffic" class="traffic-callout" />
        <router-link v-else to="/traffic" class="about-route-card traffic-callout" aria-label="Open site traffic">
          <span class="about-route-icon" aria-hidden="true"><i class="fas fa-chart-line"></i></span>
          <span class="about-route-copy">
            <strong>Site traffic</strong>
            <span>See a small record of visits and page activity</span>
          </span>
          <span class="about-route-arrow" aria-hidden="true"><ArrowUpRightIcon /></span>
        </router-link>

        <video id="videoElement" :autoplay="!story" muted loop playsinline poster="/assets/img/napier.png" preload="metadata" data-scroll-pop="true">
          <source src="/assets/vid/napier.mp4" type="video/mp4">
        </video>
        <img src="/assets/img/napier.png" alt="Travel" class="fallback-image" loading="lazy" decoding="async" style="display: none;">
      </section>

      <teleport to="body">
        <transition name="preview-fade">
          <div v-if="preview" class="page-preview" :style="previewStyle" @mouseenter="keepPreview" @mouseleave="dismissPreview">
            <span class="page-preview-label">
              <span>{{ previewLabel(preview) }}</span>
              <router-link
                class="page-preview-open"
                :to="previewRoute(preview)"
                :aria-label="'Open ' + previewLabel(preview)"
                @click="dismissPreview"
              >
                Open
                <i class="fas fa-arrow-right" aria-hidden="true"></i>
              </router-link>
            </span>
            <iframe
              v-if="previewIframeSrc(preview)"
              :key="preview"
              :src="previewIframeSrc(preview)"
              :title="previewLabel(preview) + ' preview'"
              loading="lazy"
              tabindex="-1"
            ></iframe>
          </div>
        </transition>
      </teleport>
    </article>
  </div>
</template>

<script>
  import { supportsDesktopHover, watchDesktopHover } from '../utils/desktopHover';
  import scrollRevealMixin from '../mixins/scrollRevealMixin';
  import AboutAiEntry from './AboutAiEntry.vue';
  import HomeRoutePortal from './ui/HomeRoutePortal.vue';
  import { observePortraitLayout } from '../utils/portraitLayout';
  import { enableMouseDragRail } from '../utils/mouseDragRail';

  export default {
    name: 'AboutTab',
    props: { story: { type: Boolean, default: false } },
    computed: { scrollRevealEnabled() { return !this.story; } },
    components: { AboutAiEntry, HomeRoutePortal },
    mixins: [scrollRevealMixin],
    data() {
      return {
        scriptModule: null,
        photoLoaded: false,
        preview: '',
        previewStyle: null,
      };
    },
    async mounted() {
      this._mouseGallery = enableMouseDragRail(this.$refs.photoGallery, { momentum: this.story });
      this._stopHover = watchDesktopHover(enabled => { if (!enabled) this.dismissPreview(); });
      if (!this.story) {
      this._stopPortraitLayout = observePortraitLayout(this.$refs.intro, this.$refs.introCopy, this.$refs.photo);
      this._stopResearchLayout = observePortraitLayout(this.$refs.researchIntro, this.$refs.researchCopy, this.$refs.researchIllustration, {
        property: '--about-research-image-width',
        minWidth: 769,
        fallbackAspectRatio: 1,
        maxFraction: 0.54,
      });
      }
      if (this.$refs.photo && this.$refs.photo.complete) {
        this.photoLoaded = true;
      }

      this.scriptModule = await import('../assets/js/about.js');
      if (this.scriptModule && this.scriptModule.initScript) {
        this.scriptModule.initScript();
      }
      this._onPreviewDismiss = () => this.hidePreview();
      window.addEventListener('scroll', this._onPreviewDismiss, { passive: true });
      window.addEventListener('resize', this._onPreviewDismiss, { passive: true });
    },
    beforeUnmount() {
      this._mouseGallery?.destroy();
      this._stopHover?.();
      this._stopPortraitLayout?.();
      this._stopResearchLayout?.();
    },
    unmounted() {
      window.removeEventListener('scroll', this._onPreviewDismiss);
      window.removeEventListener('resize', this._onPreviewDismiss);
      if (this.scriptModule && this.scriptModule.cleanupScript) {
        this.scriptModule.cleanupScript();
      }
      this.scriptModule = null;
    },
    methods: {
      movePhotoGallery(direction) {
        this._mouseGallery?.move(direction);
      },
      hoverPhotoGallery(direction, event) {
        if (event.pointerType === 'mouse') this._mouseGallery?.hover(direction);
      },
      stopPhotoGalleryHover() {
        this._mouseGallery?.stopHover();
      },
      leavePhotoGallery() {
        this._mouseGallery?.stopHover();
        if (this.$el.contains(document.activeElement)
          && document.activeElement?.classList.contains('home-photo-gallery-control')) {
          document.activeElement.blur();
        }
      },
      previewLabel(key) {
        return {
          projects: 'Builds · Things I\'ve Built',
          fragments: 'Fragments · Thoughts and life',
          research: 'Research · Minds and models',
        }[key] || 'Preview';
      },
      previewIframeSrc(key) {
        return {
          projects: '#/projects?preview=1',
          fragments: '#/fragments?preview=1',
          research: '#/research?preview=1',
        }[key] || null;
      },
      previewRoute(key) {
        return {
          projects: '/projects',
          fragments: '/fragments',
          research: '/research',
        }[key] || '/';
      },
      showPreview(key, event) {
        if (!supportsDesktopHover()) return;
        if (this._previewHideTimer) {
          clearTimeout(this._previewHideTimer);
          this._previewHideTimer = null;
        }
        const host = event && event.currentTarget;
        if (host) {
          const rect = host.getBoundingClientRect();
          const width = Math.min(430, window.innerWidth - 32);
          // Horizontal: pull the preview toward the page center, but never
          // detach it from the hovered element. A strip of the preview always
          // overlaps the host, so the window reads as belonging to the link
          // even when it sits near the middle of the screen.
          const minOverlap = Math.min(64, Math.round(width * 0.14));
          const centerLeft = (window.innerWidth - width) / 2;
          const minLeft = Math.max(16, rect.left + minOverlap - width);
          const maxLeft = Math.min(
            window.innerWidth - width - 16,
            rect.right - minOverlap
          );
          const left = minLeft <= maxLeft
            ? Math.max(minLeft, Math.min(centerLeft, maxLeft))
            : Math.max(16, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 16));
          this.previewStyle = { top: '0px', left: `${left}px` };
          this.preview = key;
          this.$nextTick(() => {
            const el = document.querySelector('.page-preview');
            if (!el) return;
            const height = el.offsetHeight;
            const below = rect.bottom + 12;
            const fitsBelow = below + height <= window.innerHeight - 8;
            const top = fitsBelow
              ? below
              : Math.max(8, rect.top - height - 12);
            this.previewStyle = { top: `${top}px`, left: `${left}px` };
          });
          return;
        }
        this.preview = key;
      },
      hidePreview() {
        if (this._previewHideTimer) {
          clearTimeout(this._previewHideTimer);
        }
        // A short grace lets the pointer travel from the host into the
        // teleported preview before it closes.
        this._previewHideTimer = setTimeout(() => {
          this._previewHideTimer = null;
          this.preview = '';
        }, 120);
      },
      keepPreview() {
        if (this._previewHideTimer) {
          clearTimeout(this._previewHideTimer);
          this._previewHideTimer = null;
        }
      },
      dismissPreview() {
        if (this._previewHideTimer) {
          clearTimeout(this._previewHideTimer);
          this._previewHideTimer = null;
        }
        this.preview = '';
      },
    },
  }
</script>

<style>
  .about-life-notes,
    .about-research-chapter,
  .about-build-chapter { display: contents; }
  video, .fallback-image {
      width: 100%;
  }
  .fallback-image {
      /* Keep the fallback square like the video; the global img rule rounds. */
      border-radius: 0;
  }

  .about-route-card {
      position: relative;
      display: grid;
      grid-template-columns: 1.45rem minmax(0, 1fr) auto;
      align-items: center;
      gap: 0.8rem;
      box-sizing: border-box;
      margin: 1.05rem 0 1.25rem;
      min-height: 4.1rem;
      padding: 0.72rem 0.7rem;
      border: 0;
      border-radius: 8px;
      background-color: transparent;
      color: #3d3d40;
      font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
      text-decoration: none;
      overflow: hidden;
      transition:
          color 180ms ease,
          background-color 180ms ease;
  }

  .about-route-card::before,
  .about-route-card::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      height: 1px;
      background-color: rgba(60, 60, 67, 0.12);
      pointer-events: none;
  }

  .about-route-card::before { top: 0; }
  .about-route-card::after { bottom: 0; }

  .about-route-card:hover {
      color: #171719;
      background-color: rgba(118, 118, 128, 0.075);
  }

  .about-route-card:focus-visible {
      color: #171719;
      background-color: rgba(118, 118, 128, 0.075);
      outline: 2px solid rgba(60, 60, 67, 0.44);
      outline-offset: 2px;
  }

  .about-route-card:active {
      background-color: rgba(118, 118, 128, 0.12);
  }

  .about-route-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.45rem;
      height: 1.45rem;
      color: #737378;
      font-size: 0.86rem;
      transition: color 180ms ease;
  }

  .about-route-icon i,
  .about-route-icon i:hover {
      padding: 0;
      color: inherit;
      transition: none;
  }

  .about-route-card:hover .about-route-icon,
  .about-route-card:focus-visible .about-route-icon {
      color: #252527;
  }

  .about-route-copy {
      display: grid;
      gap: 0.12rem;
      min-width: 0;
  }

  .about-route-copy strong {
      font-size: 0.84rem;
      font-weight: 650;
      letter-spacing: -0.01em;
      line-height: 1.3;
  }

  .about-route-copy span {
      color: #747479;
      font-size: 0.72rem;
      line-height: 1.4;
      transition: color 180ms ease;
  }

  .about-route-arrow {
      color: #8a8a8f;
      font-size: 0.82rem;
      line-height: 1;
      transition: color 180ms ease, transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .about-route-card:hover .about-route-arrow,
  .about-route-card:focus-visible .about-route-arrow {
      color: #3a3a3d;
      transform: translate(1px, -1px);
  }

  body.dark-mode .about-route-card {
      color: #e2e2e5;
  }

  body.dark-mode .about-route-card::before,
  body.dark-mode .about-route-card::after {
      background-color: rgba(255, 255, 255, 0.13);
  }

  body.dark-mode .about-route-card:hover,
  body.dark-mode .about-route-card:focus-visible {
      color: #f5f5f7;
      background-color: rgba(255, 255, 255, 0.075);
  }

  body.dark-mode .about-route-card:focus-visible {
      outline-color: rgba(255, 255, 255, 0.52);
  }

  body.dark-mode .about-route-card:active {
      background-color: rgba(255, 255, 255, 0.12);
  }

  body.dark-mode .about-route-icon {
      color: #c7c7cc;
  }

  body.dark-mode .about-route-card:hover .about-route-icon,
  body.dark-mode .about-route-card:focus-visible .about-route-icon {
      color: #f5f5f7;
  }

  body.dark-mode .about-route-copy span { color: var(--site-text-secondary); }
  body.dark-mode .about-route-arrow { color: var(--site-text-tertiary); }
  body.dark-mode .about-route-card:hover .about-route-arrow,
  body.dark-mode .about-route-card:focus-visible .about-route-arrow { color: #f5f5f7; }

  @media (prefers-reduced-motion: reduce) {
      .about-route-card,
      .about-route-icon,
      .about-route-arrow { transition-duration: 0.01ms; }
  }

  .photo-frame {
      position: relative;
      display: inline-block;
      max-width: 100%;
      line-height: 0;
  }

  .photo-frame img {
      display: block;
      border-radius: 5px;
  }

  .photo-watermark {
      position: absolute;
      right: clamp(12px, 3.5%, 22px);
      bottom: clamp(12px, 3.5%, 22px);
      opacity: 0;
      transition: opacity 0.5s ease;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      text-align: right;
      line-height: 1.2;
      pointer-events: none;
      user-select: none;
  }

  .photo-watermark.wm-visible {
      opacity: 1;
  }

  .photo-watermark .wm-zh {
      font-family: "Noto Serif TC", "Source Han Serif TC", "Songti TC", "STSong", serif;
      font-size: clamp(8.4px, 2vw, 10.8px);
      font-weight: 500;
      letter-spacing: 0.14em;
      margin-right: -0.14em;
      color: rgba(255, 255, 255, 0.96);
      text-shadow: 0 1px 5px rgba(0, 0, 0, 0.55), 0 0 1px rgba(0, 0, 0, 0.45);
  }

  .photo-watermark .wm-en {
      font-family: "PT Serif", Georgia, serif;
      font-style: italic;
      font-size: clamp(6px, 1.26vw, 7.2px);
      margin-top: 3px;
      letter-spacing: 0.2em;
      margin-right: -0.2em;
      color: rgba(255, 255, 255, 0.88);
      text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  }

  /* Place names already receive the paragraph's animated colour. Suppress a
     second child transition while the theme changes so both remain in lockstep. */
  html.theme-transitioning .place-link,
  body.theme-transitioning .place-link {
      transition: none !important;
  }

</style>

<style scoped>
  @media (min-width: 769px) {
    .about-intro {
      display: grid;
      grid-template-columns: minmax(0, 1fr) var(--about-portrait-width, 39%);
      grid-template-areas:
        "copy portrait"
        "cv cv";
      gap: 1.4rem 1.8rem;
      margin-top: 1.2rem;
    }
    .about-intro-copy {
      grid-area: copy;
      min-width: 0;
      align-self: start;
    }
    .about-intro-copy > p:first-child { margin-top: 0; }
    .about-intro-copy > p:last-child { margin-bottom: 0; }
    .about-intro > .photo-frame {
      grid-area: portrait;
      align-self: start;
      width: 100%;
      margin: 0;
    }
    .about-intro > .photo-frame img {
      width: 100%;
      height: auto;
      aspect-ratio: 2972 / 3963;
    }
    .about-intro > .cv-callout {
      grid-area: cv;
      margin: 0 0 1.25rem;
    }
  }

  .research-with-image {
    display: flex;
    align-items: flex-start;
    gap: 1.4rem;
    margin: 1.2rem 0;
  }
  .research-with-image p {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
  }
  .research-with-image-img {
    flex: 0 0 auto;
    width: var(--about-research-image-width, 46%);
    height: auto;
    max-width: 100%;
    aspect-ratio: 1;
    border-radius: 6px;
  }
  @media (max-width: 768px) {
    .about-intro > .photo-frame {
      display: block;
      width: 100%;
      margin-inline: 0;
    }
    .about-intro > .photo-frame img {
      display: block;
      width: 100%;
      height: auto;
    }
    .research-with-image {
      flex-direction: column-reverse;
      align-items: stretch;
      gap: 1rem;
    }
    .research-with-image-img {
      max-width: 100%;
      width: 100%;
      height: auto;
    }
  }

  .about-story .contact-email {
    position: relative;
    display: inline-block;
    color: inherit;
    text-decoration: none;
    line-height: 1.25;
  }
  .about-story .contact-email::after { display: none; }
  .about-story .contact-email-text {
    display: inline-block;
    text-decoration: none;
    transition: transform 220ms cubic-bezier(.22,1,.36,1), text-decoration-color 180ms ease;
  }
  .about-story .contact-email-mark {
    position: absolute;
    width: .8em;
    height: .64em;
    top: .3em;
    right: -1.35em;
    opacity: 0;
    transform: translate(-3px, 1px);
    pointer-events: none;
    transition: opacity 180ms ease, transform 220ms cubic-bezier(.22,1,.36,1);
  }
  @media (min-width: 761px) and (hover: hover) and (pointer: fine) and (not (any-pointer: coarse)) {
    .about-story .contact-email:hover .contact-email-text {
      transform: translateY(-1px);
      text-decoration-color: transparent;
    }
    .about-story .contact-email:hover .contact-email-mark {
      opacity: .65;
      transform: translate(0, 0);
    }
  }
  @media (max-width: 760px) {
    .about-story .contact-email:active .contact-email-text {
      transform: translateY(-1px);
      text-decoration-color: transparent;
    }
    .about-story .contact-email:active .contact-email-mark {
      opacity: .65;
      transform: translate(0, 0);
    }
  }
  .about-story .contact-email:focus-visible { outline: 1px solid currentColor; outline-offset: 4px; }
  @media (prefers-reduced-motion: reduce) {
    .about-story .contact-email-text,
    .about-story .contact-email-mark { transition: none; }
    .about-story .contact-email:hover .contact-email-text { transform: none; }
  }

  .about-story .preview-link {
    position: relative;
    display: inline-block;
    isolation: isolate;
    color: inherit;
    line-height: 1.25;
    text-decoration: none;
  }
  .about-story .preview-link::before,
  .about-story .preview-link::after { display: none; }
  .about-story .preview-link-word {
    display: inline-block;
    transition: transform 240ms cubic-bezier(.22,1,.36,1);
  }
  .about-story .preview-link-mark {
    position: absolute;
    top: -0.04em;
    right: -0.48em;
    width: .48em;
    height: .48em;
    opacity: 0;
    transform: translate(-3px, 3px);
    pointer-events: none;
    transition: transform 240ms cubic-bezier(.22,1,.36,1), opacity 160ms ease;
  }
  .about-story .preview-link.is-preview-active .preview-link-word { transform: translateY(-1px); }
  .about-story .preview-link.is-preview-active .preview-link-mark,
  .about-story .preview-link:focus-visible .preview-link-mark {
    opacity: .72;
    transform: translate(0, 0);
  }
  @media (max-width: 760px) {
    .about-story .preview-link:active .preview-link-word { transform: translateY(-1px); }
    .about-story .preview-link:active .preview-link-mark { opacity: .72; transform: translate(0, 0); }
  }
  .about-story .preview-link:focus-visible { outline: 1px solid currentColor; outline-offset: 4px; }
  .about-story .preview-link:focus-visible::before,
  .about-story .preview-link:focus-visible .preview-link-mark { transition: none; }
  @media (prefers-reduced-motion: reduce) {
    .about-story .preview-link::before,
    .about-story .preview-link-word,
    .about-story .preview-link-mark { transition: none; }
    .about-story .preview-link.is-preview-active .preview-link-word { transform: none; }
  }

  .place-link {
    position: relative;
    font-style: italic;
    text-decoration: none;
    color: inherit;
    transition: color var(--theme-duration, 220ms) ease;
  }
  .place-link::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1px;
    background: currentColor;
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 240ms cubic-bezier(0.23, 1, 0.32, 1);
  }
  .place-link:hover,
  .place-link:focus-visible {
    color: #000;
  }
  .place-link:hover::after,
  .place-link:focus-visible::after {
    transform: scaleX(1);
  }
  body.dark-mode .place-link:hover,
  body.dark-mode .place-link:focus-visible {
    color: #fff;
  }
  @media (prefers-reduced-motion: reduce) {
    .place-link::after {
      transition: none;
    }
  }
  .page-preview {
    position: fixed;
    z-index: 1000;
    width: min(430px, calc(100vw - 32px));
    border: 1px solid rgba(80, 80, 80, 0.18);
    border-radius: 0;
    background: #fafafb;
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.16);
    overflow: hidden;
  }
  .page-preview-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 12px;
    font-family: "Quattrocento Sans", sans-serif;
    font-size: 0.62rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #8a8a8a;
    border-bottom: 1px solid rgba(80, 80, 80, 0.12);
  }
  .page-preview-open {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 0;
    position: relative;
    font-family: "Quattrocento Sans", sans-serif;
    font-size: 0.62rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #7d7d7d;
    text-decoration: none;
    transition: color 160ms ease;
  }
  .page-preview-open::after {
    content: "";
    position: absolute;
    left: 0;
    right: 100%;
    bottom: 0;
    height: 1px;
    background: currentColor;
    transition: right 260ms cubic-bezier(0.23, 1, 0.32, 1);
  }
  .page-preview-open i {
    transition: transform 260ms cubic-bezier(0.23, 1, 0.32, 1);
  }
  .page-preview-open:hover {
    color: #333;
  }
  .page-preview-open:hover::after {
    right: 0;
  }
  .page-preview-open:hover i {
    transform: translateX(3px);
  }
  .page-preview iframe {
    display: block;
    width: 100%;
    height: 340px;
    border: 0;
    background: #fff;
  }
  body.dark-mode .page-preview {
    background: #2c2c2e;
    border-color: rgba(255, 255, 255, 0.14);
  }
  body.dark-mode .page-preview iframe {
    /* Match the previewed page's own dark background so the iframe never
       flashes white while its document is still loading. */
    background: #1c1c1e;
  }
  body.dark-mode .page-preview-label {
    color: var(--site-text-tertiary);
    border-color: rgba(255, 255, 255, 0.1);
  }
  body.dark-mode .page-preview-open {
    color: var(--site-text-secondary);
  }
  body.dark-mode .page-preview-open:hover {
    color: #ededed;
  }
  .preview-fade-enter-active,
  .preview-fade-leave-active {
    transition: opacity 0.18s ease, transform 0.2s cubic-bezier(0.23, 1, 0.32, 1);
  }
  .preview-fade-enter-from,
  .preview-fade-leave-to {
    opacity: 0;
    transform: translateY(6px);
  }
  .sp-mock {
    padding: 10px 12px 12px;
  }
  .sp-mock-input {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(80, 80, 80, 0.14);
    color: #9b9ba1;
    font-size: 0.86rem;
  }
  .sp-mock-modes {
    display: flex;
    gap: 2px;
    padding: 8px 0 10px;
  }
  .sp-mock-mode {
    font-family: "Quattrocento Sans", sans-serif;
    font-size: 0.58rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 4px 8px;
    background: rgba(118, 118, 128, 0.12);
    color: #76767d;
  }
  .sp-mock-mode.active {
    background: rgba(255, 255, 255, 0.92);
    color: #242426;
  }
  .sp-mock-result {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 7px 8px;
  }
  .sp-mock-result b {
    font-family: "PT Serif", Georgia, serif;
    font-size: 0.82rem;
    color: #1f1f1f;
  }
  .sp-mock-result span {
    font-size: 0.7rem;
    color: #777;
  }
  body.dark-mode .sp-mock-input {
    border-color: rgba(255, 255, 255, 0.12);
    color: var(--site-text-tertiary);
  }
  body.dark-mode .sp-mock-result b {
    color: #e4e4e4;
  }
  body.dark-mode .sp-mock-result span {
    color: var(--site-text-secondary);
  }
  @media (prefers-reduced-motion: reduce) {
    .preview-fade-enter-active,
    .preview-fade-leave-active {
      transition: opacity 0.12s ease;
    }
    .preview-fade-enter-from,
    .preview-fade-leave-to {
      transform: none;
    }
  }
</style>
